package com.ijoin.data.api

import com.ijoin.data.model.AuthError
import com.ijoin.data.model.AuthResponse
import com.ijoin.data.model.DeleteAccountRequest
import com.ijoin.data.model.LoginRequest
import com.ijoin.data.model.PasswordResetConfirmRequest
import com.ijoin.data.model.PasswordResetRequest
import com.ijoin.data.model.PasswordResetTokenStatus
import com.ijoin.data.model.RegisterRequest
import com.ijoin.data.model.UpdateProfileRequest
import com.ijoin.data.model.User
import com.ijoin.data.api.ApiClient.addAuthToken
import com.ijoin.data.api.ApiClient.addLanguageHeader
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.serialization.json.Json

class AuthApi {
    private val baseUrl = "${ApiClient.getBaseUrl()}/api/auth"
    private val json = Json {
        ignoreUnknownKeys = true
        prettyPrint = true
    }

    suspend fun register(request: RegisterRequest): Result<AuthResponse> {
        return try {
            val response: HttpResponse = ApiClient.client.post("$baseUrl/register") {
                addLanguageHeader()
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                Result.success(response.body<AuthResponse>())
            } else {
                Result.failure(parseError(response, "Registration failed: ${response.status.description}"))
            }
        } catch (e: Exception) {
            Result.failure(Exception("Network error: ${e.message}"))
        }
    }

    suspend fun login(request: LoginRequest): Result<AuthResponse> {
        return try {
            val response: HttpResponse = ApiClient.client.post("$baseUrl/login") {
                addLanguageHeader()
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                Result.success(response.body<AuthResponse>())
            } else {
                Result.failure(parseError(response, "Login failed: ${response.status.description}"))
            }
        } catch (e: Exception) {
            Result.failure(Exception("Network error: ${e.message}"))
        }
    }

    suspend fun refreshToken(refreshToken: String): Result<AuthResponse> {
        return try {
            val response: HttpResponse = ApiClient.client.post("$baseUrl/refresh") {
                addLanguageHeader()
                contentType(ContentType.Application.Json)
                setBody(mapOf("refreshToken" to refreshToken))
            }

            if (response.status.isSuccess()) {
                Result.success(response.body<AuthResponse>())
            } else {
                Result.failure(parseError(response, "Token refresh failed: ${response.status.description}"))
            }
        } catch (e: Exception) {
            Result.failure(Exception("Network error: ${e.message}"))
        }
    }

    suspend fun exchangeGoogle(code: String): Result<AuthResponse> {
        return try {
            val response: HttpResponse = ApiClient.client.post("${ApiClient.getBaseUrl()}/api/auth/google/exchange") {
                addLanguageHeader()
                contentType(ContentType.Application.Json)
                setBody(mapOf("code" to code))
            }

            if (response.status.isSuccess()) {
                Result.success(response.body<AuthResponse>())
            } else {
                Result.failure(parseError(response, "Google login failed: ${response.status.description}"))
            }
        } catch (e: Exception) {
            Result.failure(Exception("Network error: ${e.message}"))
        }
    }

    suspend fun exchangeApple(code: String): Result<AuthResponse> {
        return try {
            val response: HttpResponse = ApiClient.client.post("${ApiClient.getBaseUrl()}/api/auth/apple/exchange") {
                addLanguageHeader()
                contentType(ContentType.Application.Json)
                setBody(mapOf("code" to code))
            }

            if (response.status.isSuccess()) {
                Result.success(response.body<AuthResponse>())
            } else {
                Result.failure(parseError(response, "Apple login failed: ${response.status.description}"))
            }
        } catch (e: Exception) {
            Result.failure(Exception("Network error: ${e.message}"))
        }
    }

    suspend fun getCurrentUser(token: String): Result<User> {
        return try {
            val response: HttpResponse = ApiClient.executeWithTokenRefresh {
                ApiClient.client.get("$baseUrl/me") {
                    addLanguageHeader()
                    headers {
                        append(HttpHeaders.Authorization, "Bearer $token")
                    }
                }
            }

            if (response.status.isSuccess()) {
                Result.success(response.body<User>())
            } else {
                if (response.status == HttpStatusCode.Forbidden) {
                    Result.failure(Exception("Authentication expired. Please login again."))
                } else {
                    Result.failure(Exception("Failed to get current user"))
                }
            }
        } catch (e: Exception) {
            Result.failure(Exception("Network error: ${e.message}"))
        }
    }

    suspend fun logout(token: String): Result<Unit> {
        return try {
            val response: HttpResponse = ApiClient.client.post("$baseUrl/logout") {
                addLanguageHeader()
                headers {
                    append(HttpHeaders.Authorization, "Bearer $token")
                }
            }

            if (response.status.isSuccess()) {
                Result.success(Unit)
            } else {
                Result.failure(Exception("Logout failed"))
            }
        } catch (e: Exception) {
            // Even if the API call fails, we should still clear the local token
            Result.success(Unit)
        }
    }

    suspend fun updateProfile(request: UpdateProfileRequest): Result<User> {
        return try {
            val response: HttpResponse = ApiClient.executeWithTokenRefresh {
                ApiClient.client.put("$baseUrl/me") {
                    addAuthToken()
                    contentType(ContentType.Application.Json)
                    setBody(request)
                }
            }

            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(parseError(response, "Profile update failed: ${response.status.description}"))
            }
        } catch (e: Exception) {
            Result.failure(Exception("Network error: ${e.message}"))
        }
    }

    suspend fun deleteAccount(request: DeleteAccountRequest): Result<Unit> {
        return try {
            val response: HttpResponse = ApiClient.executeWithTokenRefresh {
                ApiClient.client.post("$baseUrl/me/delete") {
                    addAuthToken()
                    contentType(ContentType.Application.Json)
                    setBody(request)
                }
            }

            if (response.status.isSuccess()) {
                Result.success(Unit)
            } else {
                Result.failure(parseError(response, "Account deletion failed: ${response.status.description}"))
            }
        } catch (e: Exception) {
            Result.failure(Exception("Network error: ${e.message}"))
        }
    }

    suspend fun requestPasswordReset(email: String): Result<Unit> {
        return try {
            val response: HttpResponse = ApiClient.client.post("$baseUrl/password/reset/request") {
                addLanguageHeader()
                contentType(ContentType.Application.Json)
                setBody(PasswordResetRequest(email = email))
            }

            if (response.status.isSuccess()) {
                Result.success(Unit)
            } else {
                Result.failure(parseError(response, "Password reset request failed: ${response.status.description}"))
            }
        } catch (e: Exception) {
            Result.failure(Exception("Network error: ${e.message}"))
        }
    }

    suspend fun validatePasswordResetToken(token: String): Result<Boolean> {
        return try {
            val response: HttpResponse = ApiClient.client.get("$baseUrl/password/reset/validate") {
                addLanguageHeader()
                parameter("token", token)
            }
            if (response.status.isSuccess()) {
                val status = response.body<PasswordResetTokenStatus>()
                Result.success(status.valid)
            } else {
                Result.failure(parseError(response, "Password reset validation failed: ${response.status.description}"))
            }
        } catch (e: Exception) {
            Result.failure(Exception("Network error: ${e.message}"))
        }
    }

    suspend fun confirmPasswordReset(token: String, newPassword: String): Result<Unit> {
        return try {
            val response: HttpResponse = ApiClient.client.post("$baseUrl/password/reset/confirm") {
                addLanguageHeader()
                contentType(ContentType.Application.Json)
                setBody(
                    PasswordResetConfirmRequest(
                        token = token,
                        newPassword = newPassword
                    )
                )
            }

            if (response.status.isSuccess()) {
                Result.success(Unit)
            } else {
                Result.failure(parseError(response, "Password reset failed: ${response.status.description}"))
            }
        } catch (e: Exception) {
            Result.failure(Exception("Network error: ${e.message}"))
        }
    }

    private suspend fun parseError(response: HttpResponse, fallback: String): Exception {
        return try {
            val body = response.bodyAsText()
            val error = json.decodeFromString<AuthError>(body)
            Exception(error.error)
        } catch (e: Exception) {
            Exception(fallback)
        }
    }
}
